import { memo, useCallback, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import DealerVerificationBadge from "../DealerVerificationBadge";
import FairPriceBadge from "../FairPriceBadge";
import RiskBadge from "../RiskBadge";
import FeaturedPlacementBadge from "../FeaturedPlacementBadge";
import VehicleListingImage from "./VehicleListingImage";
import { LISTING_PLACEHOLDER_IMAGE } from "../../../constants/automapListingConstants";
import { DEALER_PROFILE_SOURCE_ACTIONS } from "../../../constants/dealerProfileContactConstants";
import { useOptionalCurrency, useLocale } from "../../../intl";
import { isConvertedDisplayCurrency } from "../../../intl/fxDisplayCopy.js";
import {
  getVehicleCardCopy,
  formatCardPriceInsight,
} from "../../../intl/automotiveCopy";
import { toVehicleCardViewModel } from "../../../utils/automapListing/listingViewModels";
import { canContactDealerForListing } from "../../../utils/dealerProfile/dealerContactRules";
import { trackDealerInventoryCardClick } from "../../../utils/dealerProfile/dealerInventoryCardAnalytics";
import {
  buildVehicleCardAnalyticsPayload,
  trackVehicleCardEvent,
  VEHICLE_CARD_ANALYTICS_EVENTS,
} from "../../../utils/automapListing/vehicleCardAnalytics";
import {
  trackDealerContactEvent,
  DEALER_CONTACT_ANALYTICS_EVENTS,
} from "../../../utils/dealerContactAnalytics";
import { trackFeaturedClick } from "../../../services/automapFeaturedPlacementApi";
import { scheduleNonBlocking } from "../../../utils/scheduleNonBlocking";
import { openAutomapWhatsappBotForListing } from "../../../utils/automap/openAutomapWhatsappBot";
import VehicleLikeButton from "../VehicleLikeButton";
import VehiclePaymentEstimateCompact from "../payment/VehiclePaymentEstimateCompact";
import "../../../styles/automapSearchResultCard.css";
import "../../../styles/automapVehicleLike.css";
import "../../../styles/automapPaymentEstimate.css";

const CARD_IMPRESSION_THRESHOLD = 0.35;

function showFeaturedLabel(featuredMeta, listing) {
  return Boolean(featuredMeta?.placementId || listing?.placementId);
}

function vehicleCardPropsAreEqual(prev, next) {
  return (
    prev.listing?.id === next.listing?.id &&
    prev.slug === next.slug &&
    prev.currency === next.currency &&
    prev.variant === next.variant &&
    prev.position === next.position &&
    prev.page === next.page &&
    prev.sourcePage === next.sourcePage &&
    prev.sourceComponent === next.sourceComponent &&
    prev.dealer?.id === next.dealer?.id &&
    prev.showDealerInfo === next.showDealerInfo &&
    prev.showContactCta === next.showContactCta &&
    prev.leadCaptureEnabled === next.leadCaptureEnabled &&
    prev.mapHighlighted === next.mapHighlighted &&
    prev.listingDomId === next.listingDomId &&
    prev.publicState?.canContactDealer === next.publicState?.canContactDealer &&
    prev.featuredMeta?.placementId === next.featuredMeta?.placementId &&
    prev.showPaymentEstimate === next.showPaymentEstimate &&
    prev.isAuthenticated === next.isAuthenticated &&
    prev.hasSavedPrefs === next.hasSavedPrefs &&
    prev.financingPrefs?.downPaymentCop === next.financingPrefs?.downPaymentCop &&
    prev.financingPrefs?.termMonths === next.financingPrefs?.termMonths &&
    prev.financingPrefs?.annualInterestRate === next.financingPrefs?.annualInterestRate
  );
}

function VehicleSearchResultCard({
  listing,
  slug = "automap",
  currency = "COP",
  variant = "search",
  position = null,
  page = 1,
  sourcePage = "search_results",
  sourceComponent: sourceComponentOverride = null,
  featuredMeta = null,
  className = "",
  dealer = null,
  publicState = null,
  showDealerInfo = true,
  showContactCta = true,
  leadCaptureEnabled = false,
  mapHighlighted = false,
  listingDomId = null,
  onContact,
  onOpenLeadCapture,
  onCardClick,
  financingPrefs,
  hasSavedPrefs = false,
  isAuthenticated = false,
  onFinancingPrefsUpdated,
  showPaymentEstimate = false,
}) {
  const currencyContext = useOptionalCurrency();
  const { t, locale } = useLocale();
  const cardCopy = useMemo(() => getVehicleCardCopy(t), [t]);
  const resolvedCurrency = currency || currencyContext?.displayCurrency || "COP";
  const formatMoney = currencyContext?.formatMoney || null;
  const isDealerInventory = variant === "dealer_inventory";
  const isHomepage = variant === "homepage";
  const vm = useMemo(
    () =>
      toVehicleCardViewModel(listing, {
        slug,
        currency: resolvedCurrency,
        formatMoney,
        t,
        locale,
      }),
    [listing, slug, resolvedCurrency, formatMoney, t, locale]
  );
  const priceAriaLabel = useMemo(() => {
    if (!isConvertedDisplayCurrency(resolvedCurrency) || !vm?.priceLabel) return undefined;
    return `${t("automotive.approximateConvertedPrice")}: ${vm.priceLabel}`;
  }, [resolvedCurrency, vm?.priceLabel, t]);
  const canContactListing = useMemo(() => {
    if (isDealerInventory && dealer) {
      if (publicState?.canContactDealer === false) return false;
      return canContactDealerForListing(dealer, listing);
    }
    return vm.canContact;
  }, [isDealerInventory, dealer, listing, publicState, vm.canContact]);
  const effectiveCanContact = isDealerInventory ? canContactListing : vm.canContact;
  const showSearchWhatsappCta = (variant === "search" || isHomepage) && showContactCta;
  const cardRef = useRef(null);
  const viewedRef = useRef(false);

  useEffect(() => {
    if (!listing?.id || viewedRef.current || !cardRef.current) return undefined;

    const fireViewed = () => {
      if (viewedRef.current) return;
      viewedRef.current = true;
      scheduleNonBlocking(() => {
        trackVehicleCardEvent(VEHICLE_CARD_ANALYTICS_EVENTS.VIEWED, listing, {
          position,
          page,
          sourcePage,
          sourceComponent:
            sourceComponentOverride || (featuredMeta ? "search_featured_card" : "vehicle_search_result_card"),
        });
      });
    };

    if (typeof IntersectionObserver === "undefined") {
      fireViewed();
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.some(
          (entry) => entry.isIntersecting && entry.intersectionRatio >= CARD_IMPRESSION_THRESHOLD
        );
        if (!visible) return;
        fireViewed();
        observer.disconnect();
      },
      { threshold: CARD_IMPRESSION_THRESHOLD }
    );

    observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, [listing, position, page, sourcePage, featuredMeta, sourceComponentOverride]);

  const analyticsBase = useMemo(
    () => ({
      position,
      page,
      sourcePage,
      sourceComponent:
        sourceComponentOverride || (featuredMeta ? "search_featured_card" : "vehicle_search_result_card"),
    }),
    [position, page, sourcePage, sourceComponentOverride, featuredMeta]
  );

  const handleDetailClick = useCallback(() => {
    if (featuredMeta?.placementId) {
      trackFeaturedClick({
        placementId: featuredMeta.placementId,
        slotKey: featuredMeta.slotKey,
        listingId: listing.vehicleListingId || listing.id,
        pageType: "search",
        sourceComponent: "search_featured_card",
        sourceAction: "detail_click",
      });
    }
    trackVehicleCardEvent(VEHICLE_CARD_ANALYTICS_EVENTS.CLICKED, listing, analyticsBase);
    if (isDealerInventory && dealer) {
      trackDealerInventoryCardClick(dealer, listing, analyticsBase);
    }
    onCardClick?.(listing);
  }, [featuredMeta, listing, analyticsBase, onCardClick, isDealerInventory, dealer]);

  const handleSearchWhatsappClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      trackVehicleCardEvent(VEHICLE_CARD_ANALYTICS_EVENTS.WHATSAPP_CLICKED, listing, analyticsBase);
      trackDealerContactEvent(DEALER_CONTACT_ANALYTICS_EVENTS.WHATSAPP_CLICKED, listing, dealer, {
        ...analyticsBase,
        sourceAction: "contactar_whatsapp",
        contactMethod: "whatsapp",
      });

      openAutomapWhatsappBotForListing({
        listing,
        slug,
        locale,
        currency: resolvedCurrency,
        formatMoney,
        leadType: "listing_inquiry",
        sourceAction: "contactar_whatsapp",
        source: "search_result_card",
      });
    },
    [listing, dealer, analyticsBase, slug, locale, resolvedCurrency, formatMoney]
  );

  const handleHomepageWhatsappClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      trackVehicleCardEvent(VEHICLE_CARD_ANALYTICS_EVENTS.WHATSAPP_CLICKED, listing, analyticsBase);
      trackDealerContactEvent(DEALER_CONTACT_ANALYTICS_EVENTS.WHATSAPP_CLICKED, listing, dealer, {
        ...analyticsBase,
        sourceAction: "contactar_whatsapp",
        contactMethod: "whatsapp",
      });

      openAutomapWhatsappBotForListing({
        listing,
        slug,
        locale,
        currency: resolvedCurrency,
        formatMoney,
        leadType: "listing_inquiry",
        sourceAction: "contactar_whatsapp",
        source: "homepage_vehicle_card",
      });
    },
    [listing, dealer, analyticsBase, slug, locale, resolvedCurrency, formatMoney]
  );

  const handleWhatsAppClick = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!effectiveCanContact) {
        trackDealerContactEvent(DEALER_CONTACT_ANALYTICS_EVENTS.CONTACT_DISABLED_CLICKED, listing, dealer, {
          ...analyticsBase,
          sourceAction: isDealerInventory
            ? DEALER_PROFILE_SOURCE_ACTIONS.CONTACT_DISABLED_CLICK
            : "whatsapp_click",
          contactMethod: "whatsapp",
        });
        if (!isDealerInventory && vm.hasDetailLink) handleDetailClick();
        return;
      }
      trackVehicleCardEvent(VEHICLE_CARD_ANALYTICS_EVENTS.WHATSAPP_CLICKED, listing, analyticsBase);
      trackDealerContactEvent(DEALER_CONTACT_ANALYTICS_EVENTS.WHATSAPP_CLICKED, listing, dealer, {
        ...analyticsBase,
        sourceAction: isDealerInventory
          ? DEALER_PROFILE_SOURCE_ACTIONS.INVENTORY_VEHICLE_CONTACT_CLICK
          : "whatsapp_click",
        contactMethod: "whatsapp",
      });

      const opened = openAutomapWhatsappBotForListing({
        listing,
        slug,
        locale,
        currency: resolvedCurrency,
        formatMoney,
        dealer,
        leadType: isDealerInventory ? "vehicle_listing_contact" : "listing_inquiry",
        sourceAction: isDealerInventory
          ? DEALER_PROFILE_SOURCE_ACTIONS.INVENTORY_VEHICLE_CONTACT_CLICK
          : "contactar_whatsapp",
        source: analyticsBase.sourceComponent || "vehicle_card",
      });

      if (!opened && onContact) {
        onContact({
          listing,
          leadType: isDealerInventory ? "vehicle_listing_contact" : "listing_inquiry",
          sourceAction: isDealerInventory
            ? DEALER_PROFILE_SOURCE_ACTIONS.INVENTORY_VEHICLE_CONTACT_CLICK
            : "contactar_whatsapp",
          sourceComponent: analyticsBase.sourceComponent,
          sourcePage,
          directContact: true,
          openWhatsApp: true,
        });
      }
    },
    [
      effectiveCanContact,
      isDealerInventory,
      vm.hasDetailLink,
      listing,
      dealer,
      analyticsBase,
      sourcePage,
      onContact,
      slug,
      locale,
      resolvedCurrency,
      formatMoney,
      handleDetailClick,
    ]
  );

  const handleDealerClick = useCallback(
    (event) => {
      event.stopPropagation();
      trackVehicleCardEvent(VEHICLE_CARD_ANALYTICS_EVENTS.DEALER_CLICKED, listing, analyticsBase);
      trackDealerContactEvent(DEALER_CONTACT_ANALYTICS_EVENTS.PROFILE_CLICKED, listing, null, {
        ...analyticsBase,
        sourceAction: "dealer_profile_click",
      });
    },
    [listing, analyticsBase]
  );

  const isFeatured = showFeaturedLabel(featuredMeta, listing);
  const isCompact = variant === "compact" || variant === "recommendation";

  const specItems = useMemo(() => {
    const items = [
      { key: "year", label: cardCopy.specYear, value: vm.yearLabel },
      { key: "mileage", label: cardCopy.specMileage, value: vm.mileageLabel },
      { key: "transmission", label: cardCopy.specTransmission, value: vm.transmissionLabel },
      { key: "body", label: cardCopy.specBody, value: vm.bodyTypeLabel },
    ];
    if (isDealerInventory) {
      items.push({ key: "fuel", label: t("automotive.fuelType"), value: vm.fuelTypeLabel });
    }
    return items;
  }, [
    cardCopy,
    vm.yearLabel,
    vm.mileageLabel,
    vm.transmissionLabel,
    vm.bodyTypeLabel,
    vm.fuelTypeLabel,
    isDealerInventory,
    t,
  ]);

  const priceInsight = useMemo(
    () => formatCardPriceInsight(listing, t, locale),
    [listing, t, locale]
  );

  const photoCount = listing?.imageCount || listing?.images?.length || 0;
  const showSaveButton = variant === "search" || isHomepage;

  const detailLinkProps = vm.hasDetailLink
    ? { to: vm.href, onClick: handleDetailClick }
    : { to: "#", onClick: (event) => event.preventDefault(), "aria-disabled": true };

  const dealerNameNode = vm.dealerProfileHref ? (
    <Link
      to={vm.dealerProfileHref}
      className="vehicle-search-card__dealer-link"
      onClick={handleDealerClick}
    >
      {vm.dealerName}
    </Link>
  ) : (
    <strong>{vm.dealerName}</strong>
  );

  if (isDealerInventory) {
    return (
      <article
        ref={cardRef}
        className={`vehicle-search-card vehicle-search-card--dealer-inventory ${className}`.trim()}
        data-listing-id={listing?.id}
        data-analytics={JSON.stringify(buildVehicleCardAnalyticsPayload(listing, analyticsBase))}
      >
        <div className="vehicle-search-card__media vehicle-listing-image-frame">
          <Link
            {...detailLinkProps}
            className="vehicle-search-card__media-link"
            aria-label={cardCopy.viewDetailAria(vm.title)}
          >
            <VehicleListingImage
              listing={listing}
              primaryImageUrl={vm.primaryImageUrl || LISTING_PLACEHOLDER_IMAGE}
              className="vehicle-search-card__image"
            />
          </Link>
          <span className="vehicle-search-card__freshness photo-badge">{vm.freshnessLabel}</span>
          {photoCount > 1 ? (
            <span className="vehicle-search-card__photo-count">{cardCopy.photos(photoCount)}</span>
          ) : null}
        </div>

        <div className="vehicle-search-card__content vehicle-search-card__content--dealer-inventory">
          <div className="vehicle-search-card__header vehicle-search-card__header--dealer-inventory">
            <div className="vehicle-search-card__header-main">
              <h3 className="vehicle-search-card__title">
                <Link {...detailLinkProps}>{vm.title}</Link>
              </h3>
              <p className="vehicle-search-card__location">{vm.locationLabel}</p>
            </div>
            <div className="vehicle-search-card__price-block">
              <p className="vehicle-search-card__price price" aria-label={priceAriaLabel}>{vm.priceLabel}</p>
              {priceInsight ? (
                <p className="vehicle-search-card__price-insight">{priceInsight}</p>
              ) : null}
            </div>
          </div>

          {specItems.length ? (
            <dl className="vehicle-search-card__spec-grid vehicle-search-card__spec-grid--dealer-inventory">
              {specItems.map((item) => (
                <div key={item.key} className="vehicle-search-card__spec-cell">
                  <dt className="vehicle-search-card__spec-label">{item.label}</dt>
                  <dd className="vehicle-search-card__spec-value">{item.value}</dd>
                </div>
              ))}
            </dl>
          ) : vm.specsRowLabel ? (
            <p className="vehicle-search-card__specs">{vm.specsRowLabel}</p>
          ) : null}

          <div className="vehicle-search-card__footer vehicle-search-card__footer--dealer-inventory">
            <div className="vehicle-search-card__footer-meta">
              {vm.lastConfirmedLabel ? (
                <p className="vehicle-search-card__meta-line">{vm.lastConfirmedLabel}</p>
              ) : null}
              {listing?.offersFinancing ? (
                <span className="vehicle-search-card__financing-badge">Financiación</span>
              ) : null}
            </div>

            <div className="vehicle-search-card__trust badges">
              <FairPriceBadge
                listing={listing}
                size="small"
                variant="card"
                context="card"
                onBadgeClick={() => {
                  trackVehicleCardEvent(
                    VEHICLE_CARD_ANALYTICS_EVENTS.FAIR_PRICE_BADGE_CLICKED,
                    listing,
                    analyticsBase
                  );
                }}
              />
              <RiskBadge
                listing={listing}
                size="small"
                variant="dealer_inventory"
                context="dealer_profile"
                hideIfLow
                onBadgeClick={() => {
                  trackVehicleCardEvent(
                    VEHICLE_CARD_ANALYTICS_EVENTS.RISK_BADGE_CLICKED,
                    listing,
                    analyticsBase
                  );
                }}
              />
            </div>

            <div className="actions vehicle-search-card__actions vehicle-search-card__actions--dealer-inventory">
              <Link className="btn btn-outline" {...detailLinkProps}>
                {cardCopy.viewDetail}
              </Link>
              {showContactCta ? (
                <button
                  type="button"
                  className="btn btn-green"
                  disabled={!effectiveCanContact && !vm.hasDetailLink}
                  title={
                    effectiveCanContact ? cardCopy.contactWhatsapp : cardCopy.contactUnavailableDetail
                  }
                  onClick={handleWhatsAppClick}
                  aria-label={
                    effectiveCanContact ? cardCopy.contactWhatsapp : cardCopy.contactUnavailable
                  }
                >
                  {effectiveCanContact ? cardCopy.whatsapp : cardCopy.contactUnavailable}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      </article>
    );
  }

  if (isCompact) {
    return (
      <article
        ref={cardRef}
        className={`vehicle-search-card vehicle-search-card--compact seo-inventory-card${
          isFeatured ? " featured-placement-card" : ""
        } ${className}`.trim()}
        data-listing-id={listing?.id}
      >
        <Link {...detailLinkProps} className="seo-inventory-card__link vehicle-search-card__compact-link">
          <div className="seo-inventory-card__photo vehicle-search-card__media vehicle-listing-image-frame">
            <VehicleListingImage
              listing={listing}
              primaryImageUrl={vm.primaryImageUrl || LISTING_PLACEHOLDER_IMAGE}
              className="vehicle-search-card__image"
            />
            {isFeatured ? (
              <FeaturedPlacementBadge className="featured-placement-badge--overlay" />
            ) : (
              <span className="vehicle-search-card__freshness photo-badge">{vm.freshnessLabel}</span>
            )}
          </div>
          <div className="seo-inventory-card__body vehicle-search-card__body">
            <p className="vehicle-search-card__price price" aria-label={priceAriaLabel}>{vm.priceLabel}</p>
            <h3 className="vehicle-search-card__title">{vm.title}</h3>
            {vm.specsRowLabel ? (
              <p className="seo-inventory-card__meta vehicle-search-card__specs">{vm.specsRowLabel}</p>
            ) : (
              <p className="seo-inventory-card__meta">{vm.locationLabel}</p>
            )}
            <div className="seo-inventory-card__badges vehicle-search-card__trust">
              <FairPriceBadge
                listing={listing}
                size="small"
                variant="card"
                context="card"
                onBadgeClick={() => {
                  trackVehicleCardEvent(
                    VEHICLE_CARD_ANALYTICS_EVENTS.FAIR_PRICE_BADGE_CLICKED,
                    listing,
                    analyticsBase
                  );
                }}
              />
              <RiskBadge
                listing={listing}
                size="small"
                variant="search_card"
                context="card"
                hideIfLow
                onBadgeClick={() => {
                  trackVehicleCardEvent(
                    VEHICLE_CARD_ANALYTICS_EVENTS.RISK_BADGE_CLICKED,
                    listing,
                    analyticsBase
                  );
                }}
              />
            </div>
            <div className="seo-inventory-card__dealer vehicle-search-card__dealer">
              <span className="seo-inventory-card__avatar avatar">{vm.dealerInitials}</span>
              <span>{vm.dealerName}</span>
              <DealerVerificationBadge listing={listing} size="small" variant="card" context="card" />
            </div>
          </div>
        </Link>
      </article>
    );
  }

  if (isHomepage) {
    return (
      <article
        ref={cardRef}
        id={listingDomId || undefined}
        className={`vehicle-search-card vehicle-search-card--homepage${
          isFeatured ? " vehicle-search-card--featured" : ""
        } ${className}`.trim()}
        data-listing-id={listing?.id}
        data-analytics={JSON.stringify(buildVehicleCardAnalyticsPayload(listing, analyticsBase))}
      >
        <div className="vehicle-search-card__media vehicle-listing-image-frame">
          <Link
            {...detailLinkProps}
            className="vehicle-search-card__media-link"
            aria-label={cardCopy.viewDetailAria(vm.title)}
          >
            <VehicleListingImage
              listing={listing}
              primaryImageUrl={vm.primaryImageUrl || LISTING_PLACEHOLDER_IMAGE}
              className="vehicle-search-card__image"
            />
          </Link>
          {isFeatured ? (
            <FeaturedPlacementBadge className="featured-placement-badge--overlay" />
          ) : (
            <span className="vehicle-search-card__freshness">{vm.freshnessLabel}</span>
          )}
          {photoCount > 1 ? (
            <span className="vehicle-search-card__photo-count">{cardCopy.photos(photoCount)}</span>
          ) : null}
          {showSaveButton ? (
            <VehicleLikeButton
              listing={listing}
              className="vehicle-search-card__like-btn"
              sourceComponent="homepage_featured_card"
              sourcePage={sourcePage}
            />
          ) : null}
        </div>

        <div className="vehicle-search-card__content">
          <div className="vehicle-search-card__main">
            <header className="vehicle-search-card__identity">
              <div className="vehicle-search-card__identity-top">
                <h3 className="vehicle-search-card__title">
                  <Link {...detailLinkProps}>{vm.title}</Link>
                </h3>
                <p className="vehicle-search-card__price-value" aria-label={priceAriaLabel}>
                  {vm.priceLabel}
                </p>
              </div>
              <p className="vehicle-search-card__location">{vm.locationLabel}</p>
              <div className="vehicle-search-card__badges">
                <FairPriceBadge
                  listing={listing}
                  size="small"
                  variant="card"
                  context="card"
                  onBadgeClick={() => {
                    trackVehicleCardEvent(
                      VEHICLE_CARD_ANALYTICS_EVENTS.FAIR_PRICE_BADGE_CLICKED,
                      listing,
                      analyticsBase
                    );
                  }}
                />
                <DealerVerificationBadge listing={listing} size="small" variant="card" context="card" />
              </div>
            </header>

            {specItems.length ? (
              <dl className="vehicle-search-card__spec-grid vehicle-search-card__spec-grid--homepage">
                {specItems.map((item) => (
                  <div key={item.key} className="vehicle-search-card__spec-cell">
                    <dt className="vehicle-search-card__spec-label">{item.label}</dt>
                    <dd className="vehicle-search-card__spec-value">{item.value}</dd>
                  </div>
                ))}
              </dl>
            ) : vm.specsRowLabel ? (
              <p className="vehicle-search-card__specs-fallback">{vm.specsRowLabel}</p>
            ) : null}
          </div>

          <footer className="vehicle-search-card__footer vehicle-search-card__footer--homepage">
            <div className="vehicle-search-card__actions vehicle-search-card__actions--homepage">
              <Link
                className="btn btn-outline vehicle-search-card__btn-detail"
                {...detailLinkProps}
              >
                {cardCopy.viewDetail}
              </Link>
              {showSearchWhatsappCta ? (
                <button
                  type="button"
                  className="btn btn-green vehicle-search-card__btn-contact"
                  title={cardCopy.contactWhatsapp}
                  onClick={handleHomepageWhatsappClick}
                  aria-label={cardCopy.contactWhatsapp}
                >
                  {cardCopy.whatsapp}
                </button>
              ) : null}
            </div>
          </footer>
        </div>
      </article>
    );
  }

  return (
    <article
      ref={cardRef}
      id={listingDomId || undefined}
      className={`card vehicle-search-card vehicle-search-card--search${
        isFeatured ? " card--featured vehicle-search-card--featured" : ""
      }${mapHighlighted ? " vehicle-search-card--map-highlight" : ""} ${className}`.trim()}
      data-listing-id={listing?.id}
      data-analytics={JSON.stringify(buildVehicleCardAnalyticsPayload(listing, analyticsBase))}
    >
      <div className="vehicle-search-card__media vehicle-listing-image-frame">
        <Link {...detailLinkProps} className="vehicle-search-card__media-link" aria-label={cardCopy.viewDetailAria(vm.title)}>
          <VehicleListingImage
            listing={listing}
            primaryImageUrl={vm.primaryImageUrl || LISTING_PLACEHOLDER_IMAGE}
            className="vehicle-search-card__image"
          />
        </Link>
        {isFeatured ? (
          <FeaturedPlacementBadge className="featured-placement-badge--overlay" />
        ) : (
          <span className="vehicle-search-card__freshness">{vm.freshnessLabel}</span>
        )}
        {photoCount > 1 ? (
          <span className="vehicle-search-card__photo-count">{cardCopy.photos(photoCount)}</span>
        ) : null}
        {showSaveButton ? (
          <VehicleLikeButton
            listing={listing}
            className="vehicle-search-card__like-btn"
            sourceComponent="vehicle_search_result_card"
            sourcePage={sourcePage}
          />
        ) : null}
      </div>

      <div className="vehicle-search-card__content">
        <div className="vehicle-search-card__main">
          <header className="vehicle-search-card__identity">
            <div className="vehicle-search-card__identity-top">
              <h3 className="vehicle-search-card__title">
                <Link {...detailLinkProps}>{vm.title}</Link>
              </h3>
              <div className="vehicle-search-card__price-block">
                <p className="vehicle-search-card__price-value" aria-label={priceAriaLabel}>{vm.priceLabel}</p>
                {priceInsight ? (
                  <p className="vehicle-search-card__price-insight">{priceInsight}</p>
                ) : null}
              </div>
            </div>
            <div className="vehicle-search-card__meta-row">
              <p className="vehicle-search-card__location">
                <span className="vehicle-search-card__location-icon" aria-hidden="true">
                  ◎
                </span>
                {vm.locationLabel}
              </p>
              <div className="vehicle-search-card__badges">
                <FairPriceBadge
                  listing={listing}
                  size="small"
                  variant="card"
                  context="card"
                  onBadgeClick={() => {
                    trackVehicleCardEvent(
                      VEHICLE_CARD_ANALYTICS_EVENTS.FAIR_PRICE_BADGE_CLICKED,
                      listing,
                      analyticsBase
                    );
                  }}
                />
                <RiskBadge
                  listing={listing}
                  size="small"
                  variant="search_card"
                  context="card"
                  hideIfLow
                  onBadgeClick={() => {
                    trackVehicleCardEvent(
                      VEHICLE_CARD_ANALYTICS_EVENTS.RISK_BADGE_CLICKED,
                      listing,
                      analyticsBase
                    );
                  }}
                />
              </div>
            </div>
          </header>

          {showPaymentEstimate ? (
            <div className="vehicle-search-card__payment">
              <VehiclePaymentEstimateCompact
                listing={listing}
                financingPrefs={financingPrefs}
                hasSavedPrefs={hasSavedPrefs}
                isAuthenticated={isAuthenticated}
                onFinancingPrefsUpdated={onFinancingPrefsUpdated}
                showGuestQuickSetup={false}
                source="search_card"
              />
            </div>
          ) : null}

          {specItems.length ? (
            <section className="vehicle-search-card__specs-panel">
              <dl className="vehicle-search-card__spec-grid">
                {specItems.map((item) => (
                  <div key={item.key} className="vehicle-search-card__spec-cell">
                    <dt className="vehicle-search-card__spec-label">{item.label}</dt>
                    <dd className="vehicle-search-card__spec-value">{item.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          ) : vm.specsRowLabel ? (
            <p className="vehicle-search-card__specs-fallback">{vm.specsRowLabel}</p>
          ) : null}
        </div>

        <footer className="vehicle-search-card__footer">
          <div className="vehicle-search-card__dealer-block">
            <span className="vehicle-search-card__avatar" aria-hidden="true">
              {vm.dealerInitials}
            </span>
            <div className="vehicle-search-card__dealer-copy">
              <div className="vehicle-search-card__dealer-name-row">
                {dealerNameNode}
                <DealerVerificationBadge listing={listing} size="small" variant="card" context="card" />
              </div>
              {[vm.dealerCityLabel, vm.dealerResponseTimeLabel, vm.lastConfirmedLabel]
                .filter(Boolean)
                .length ? (
                <span className="vehicle-search-card__dealer-meta">
                  {[vm.dealerCityLabel, vm.dealerResponseTimeLabel, vm.lastConfirmedLabel]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              ) : null}
            </div>
          </div>

          <div className="vehicle-search-card__actions vehicle-search-card__actions--search">
            <Link className="btn btn-outline vehicle-search-card__btn-detail" {...detailLinkProps}>
              {cardCopy.viewDetail}
            </Link>
            {showSearchWhatsappCta ? (
              <button
                type="button"
                className="btn btn-green vehicle-search-card__btn-contact"
                title={cardCopy.contactWhatsapp}
                onClick={handleSearchWhatsappClick}
                aria-label={cardCopy.contactWhatsapp}
              >
                {cardCopy.whatsapp}
              </button>
            ) : null}
          </div>
        </footer>
      </div>
    </article>
  );
}

export default memo(VehicleSearchResultCard, vehicleCardPropsAreEqual);
